import React, { useState, useCallback, useEffect, useMemo } from 'react';
import QuestionEditModal from './QuestionEditModal';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  Avatar,
  Card,
  CardContent
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  DragIndicator as DragIndicatorIcon,
  MusicNote as MusicNoteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useSongs } from '../../hooks/useSongs';
import { useImportQuizQuestions } from '../../hooks/useQuizQuestions';
import { getImageUrl } from '../../../utils/imageHelper';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '12px',
    maxWidth: '90vw',
    width: '1200px',
    maxHeight: '90vh',
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-root': {
    color: '#ccc',
  },
  '& .MuiStepIcon-root': {
    color: '#333',
    '&.Mui-active': {
      color: '#4CAF50',
    },
    '&.Mui-completed': {
      color: '#4CAF50',
    },
  },
}));

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? '#4CAF50' : '#333'}`,
  borderRadius: '8px',
  padding: '40px',
  textAlign: 'center',
  backgroundColor: isDragOver ? 'rgba(76, 175, 80, 0.1)' : '#121212',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.05)',
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  ...(variant === 'contained' && {
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  }),
  ...(variant === 'outlined' && {
    borderColor: '#333',
    color: '#ccc',
    '&:hover': {
      borderColor: '#4CAF50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
    },
  }),
}));

const steps = ['Select Songs', 'Upload File', 'Review & Import'];

const QuizQuestionImportModal = ({ open, onClose, onSuccess }) => {
  const { data: songs = [], isLoading: songsLoading } = useSongs();
  const importMutation = useImportQuizQuestions();

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Enhanced state for duplicate detection and row actions
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [duplicateSettings, setDuplicateSettings] = useState({
    autoRemove: false,
    showDuplicates: true
  });

  // Reset state when modal opens/closes
  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedSongs([]);
    setUploadedFile(null);
    setParsedData([]);
    setValidationResults([]);
    setIsDragOver(false);
    setIsProcessing(false);
    setDuplicateGroups([]);
    setEditingQuestion(null);
    setEditModalOpen(false);
    setDuplicateSettings({ autoRemove: false, showDuplicates: true });
    onClose();
  }, [onClose]);

  // Step navigation
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Song selection handlers
  const handleSongAdd = (song) => {
    if (!selectedSongs.find(s => s.songs_master_id === song.songs_master_id)) {
      setSelectedSongs([...selectedSongs, song]);
    }
  };

  const handleSongRemove = (songId) => {
    setSelectedSongs(selectedSongs.filter(s => s.songs_master_id !== songId));
  };

  // Handle song reorder with DnD
  const handleSongDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSelectedSongs((items) => {
      const oldIndex = items.findIndex(item => item.songs_master_id === active.id);
      const newIndex = items.findIndex(item => item.songs_master_id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  // Duplicate detection utility functions
  const detectDuplicates = useCallback((data) => {
    const questionTextMap = new Map();
    const duplicates = [];

    data.forEach((question, index) => {
      const normalizedText = question.questionText?.toLowerCase().trim();
      if (normalizedText) {
        if (questionTextMap.has(normalizedText)) {
          const existingGroup = questionTextMap.get(normalizedText);
          existingGroup.push(index);
        } else {
          questionTextMap.set(normalizedText, [index]);
        }
      }
    });

    // Find groups with more than one question (duplicates)
    questionTextMap.forEach((indices, text) => {
      if (indices.length > 1) {
        duplicates.push({
          questionText: text,
          indices: indices,
          count: indices.length
        });
      }
    });

    return duplicates;
  }, []);

  // Row action handlers
  const handleEditRow = useCallback((index) => {
    const question = parsedData[index];
    console.log('📝 Editing question:', {
      index,
      questionMood: question.questionMood,
      questionTopic: question.questionTopic,
      fullQuestion: question
    });
    setEditingQuestion({
      ...question,
      index: index
    });
    setEditModalOpen(true);
  }, [parsedData]);

  const handleSaveEdit = useCallback((editedQuestion) => {
    if (editingQuestion && editingQuestion.index !== undefined) {
      const updatedData = [...parsedData];
      updatedData[editingQuestion.index] = {
        ...editedQuestion
      };
      setParsedData(updatedData);

      // Re-validate the edited question
      const updatedValidation = [...validationResults];
      const question = editedQuestion;
      const errors = [];

      // Basic validation
      if (!question.questionText || question.questionText.trim().length < 10) {
        errors.push('Question text must be at least 10 characters');
      }

      if (!question.answerType) {
        errors.push('Answer type is required');
      }

      if (!question.difficulty) {
        errors.push('Difficulty is required');
      }

      if (!question.answers || question.answers.length < 2) {
        errors.push('At least 2 answers are required');
      }

      const correctAnswers = question.answers?.filter(a => a.correct).length || 0;
      if (correctAnswers === 0) {
        errors.push('At least one answer must be correct');
      }

      if (question.answerType === 'single_choice' && correctAnswers !== 1) {
        errors.push('Single choice questions must have exactly one correct answer');
      }

      if (question.answerType === 'multiple_choice' && correctAnswers < 1) {
        errors.push('Multiple choice questions must have at least one correct answer');
      }

      if (question.answerType === 'multiple_choice' && question.answers?.length < 3) {
        errors.push('Multiple choice questions should have at least 3 answer options');
      }

      if (question.answerType === 'true_false' && question.answers?.length !== 2) {
        errors.push('True/False questions must have exactly 2 answers');
      }

      if (question.answerType === 'true_false' && correctAnswers !== 1) {
        errors.push('True/False questions must have exactly one correct answer');
      }

      updatedValidation[editingQuestion.index] = {
        isValid: errors.length === 0,
        errors: errors
      };
      setValidationResults(updatedValidation);

      // Re-detect duplicates after edit
      const newDuplicates = detectDuplicates(updatedData);
      setDuplicateGroups(newDuplicates);

      setEditingQuestion(null);
      setEditModalOpen(false);
    }
  }, [editingQuestion, parsedData, validationResults, detectDuplicates]);

  const handleDeleteRow = useCallback((index) => {
    const updatedData = parsedData.filter((_, i) => i !== index);
    const updatedValidation = validationResults.filter((_, i) => i !== index);

    setParsedData(updatedData);
    setValidationResults(updatedValidation);

    // Re-detect duplicates after deletion
    const newDuplicates = detectDuplicates(updatedData);
    setDuplicateGroups(newDuplicates);

    // Reset editing state if we were editing the deleted row
    if (editingQuestion && editingQuestion.index === index) {
      setEditingQuestion(null);
      setEditModalOpen(false);
    } else if (editingQuestion && editingQuestion.index > index) {
      setEditingQuestion({
        ...editingQuestion,
        index: editingQuestion.index - 1
      });
    }
  }, [parsedData, validationResults, editingQuestion, detectDuplicates]);

  const handleRemoveDuplicates = useCallback((groupIndex, keepIndex) => {
    const group = duplicateGroups[groupIndex];
    if (!group) return;

    const indicesToRemove = group.indices.filter(i => i !== keepIndex);
    let updatedData = [...parsedData];
    let updatedValidation = [...validationResults];

    // Remove duplicates in reverse order to maintain indices
    indicesToRemove.sort((a, b) => b - a).forEach(index => {
      updatedData.splice(index, 1);
      updatedValidation.splice(index, 1);
    });

    setParsedData(updatedData);
    setValidationResults(updatedValidation);

    // Re-detect duplicates after removal
    const newDuplicates = detectDuplicates(updatedData);
    setDuplicateGroups(newDuplicates);

    // Reset editing state if we were editing a removed row
    if (editingQuestion) {
      if (indicesToRemove.includes(editingQuestion.index)) {
        setEditingQuestion(null);
        setEditModalOpen(false);
      } else {
        // Adjust editing index if rows before it were removed
        const removedBeforeEdit = indicesToRemove.filter(i => i < editingQuestion.index).length;
        if (removedBeforeEdit > 0) {
          setEditingQuestion({
            ...editingQuestion,
            index: editingQuestion.index - removedBeforeEdit
          });
        }
      }
    }
  }, [duplicateGroups, parsedData, validationResults, detectDuplicates, editingQuestion]);

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth={false}>
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#fff',
        borderBottom: '1px solid #333'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Import Quiz Questions from Excel
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#ccc' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Stepper */}
        <StyledStepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>

        {/* Step Content */}
        {activeStep === 0 && (
          <SongSelectionStep
            songs={songs}
            songsLoading={songsLoading}
            selectedSongs={selectedSongs}
            onSongAdd={handleSongAdd}
            onSongRemove={handleSongRemove}
            onSongReorder={handleSongDragEnd}
          />
        )}

        {activeStep === 1 && (
          <FileUploadStep
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            isDragOver={isDragOver}
            setIsDragOver={setIsDragOver}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
            setParsedData={setParsedData}
            setValidationResults={setValidationResults}
            duplicateGroups={duplicateGroups}
            setDuplicateGroups={setDuplicateGroups}
            detectDuplicates={detectDuplicates}
            selectedSongs={selectedSongs}
          />
        )}

        {activeStep === 2 && (
          <ReviewStep
            parsedData={parsedData}
            setParsedData={setParsedData}
            validationResults={validationResults}
            setValidationResults={setValidationResults}
            selectedSongs={selectedSongs}
            uploadedFile={uploadedFile}
            duplicateGroups={duplicateGroups}
            setDuplicateGroups={setDuplicateGroups}
            duplicateSettings={duplicateSettings}
            setDuplicateSettings={setDuplicateSettings}
            onEditRow={handleEditRow}
            onDeleteRow={handleDeleteRow}
            onRemoveDuplicates={handleRemoveDuplicates}
            detectDuplicates={detectDuplicates}
            importMutation={importMutation}
            onSuccess={onSuccess}
            onClose={handleClose}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #333' }}>
        <ActionButton variant="outlined" onClick={handleClose}>
          Cancel
        </ActionButton>

        {activeStep > 0 && (
          <ActionButton variant="outlined" onClick={handleBack}>
            Back
          </ActionButton>
        )}

        {activeStep < steps.length - 1 && (
          <ActionButton
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && selectedSongs.length === 0) ||
              (activeStep === 1 && (!uploadedFile || parsedData.length === 0))
            }
          >
            Next
          </ActionButton>
        )}

        {activeStep === steps.length - 1 && (
          <ImportButton
            uploadedFile={uploadedFile}
            parsedData={parsedData}
            selectedSongs={selectedSongs}
            importMutation={importMutation}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        )}
      </DialogActions>

      {/* Question Edit Modal */}
      <QuestionEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        question={editingQuestion}
        onSave={handleSaveEdit}
        validationErrors={editingQuestion && validationResults[editingQuestion.index] ? validationResults[editingQuestion.index].errors : []}
      />
    </StyledDialog>
  );
};

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Sortable Song Item Component
const SortableSongItem = ({ song, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: song.songs_master_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 2,
        backgroundColor: '#1A1A1A',
        border: '1px solid #333',
        '&:hover': {
          borderColor: '#4CAF50',
        },
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: '#ccc',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon />
        </Box>
        <Avatar
          src={getImageUrl(song.image)}
          alt={song.name}
          sx={{ width: 56, height: 56 }}
        >
          <MusicNoteIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>
            {song.name}
          </Typography>
          {song.artist && (
            <Typography variant="caption" sx={{ color: '#ccc' }}>
              by {song.artist}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={() => onRemove(song.songs_master_id)}
          sx={{ color: '#f44336' }}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      </CardContent>
    </Card>
  );
};

// Song Selection Step Component
const SongSelectionStep = ({ songs, songsLoading, selectedSongs, onSongAdd, onSongRemove, onSongReorder }) => {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 300);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter songs based on search term
  const filteredSongs = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
      // Return all songs when no search term, sorted alphabetically
      // return songs.sort((a, b) => {
      //   const aName = (a.name || '').toLowerCase();
      //   const bName = (b.name || '').toLowerCase();
      //   return aName.localeCompare(bName);
      // });
      return songs;
    }

    const searchTerm = debouncedSearchTerm.toLowerCase().trim();
    const searchWords = searchTerm.split(' ').filter(word => word.length > 0);

    const filtered = songs.filter(song => {
      const songName = (song.name || '').toLowerCase();
      const artistName = (song.artist || '').toLowerCase();

      // Check if all search words are found in either song name or artist
      return searchWords.every(word =>
        songName.includes(word) || artistName.includes(word)
      );
    });

    // Sort filtered results by relevance
    return filtered.sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();

      // Exact matches first
      if (aName === searchTerm) return -1;
      if (bName === searchTerm) return 1;

      // Starts with search term
      if (aName.startsWith(searchTerm) && !bName.startsWith(searchTerm)) return -1;
      if (bName.startsWith(searchTerm) && !aName.startsWith(searchTerm)) return 1;

      // Alphabetical fallback
      return aName.localeCompare(bName);
    });
  }, [songs, debouncedSearchTerm]);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        Select Songs for Question Import
      </Typography>

      <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
        Select one or more songs. Questions from the Excel file will be assigned to these songs.
      </Alert>

      {/* Song Search */}
      <Autocomplete
        options={filteredSongs}
        getOptionLabel={(song) => song.name || ""}
        getOptionKey={(song) => `song-${song.songs_master_id}-${song.name}`} // Unique key
        onChange={(_, newValue) => {
          if (newValue) {
            onSongAdd(newValue);
            setSearchInput(''); // Clear search after selection
          }
        }}
        onInputChange={(event, newInputValue) => {
          setSearchInput(newInputValue);
        }}
        inputValue={searchInput}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search and select songs"
            placeholder="Type song name or artist..."
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#121212',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#4CAF50' },
                '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
              },
              '& .MuiInputLabel-root': { color: '#ccc' },
              '& .MuiOutlinedInput-input': { color: '#fff' },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={`song-${option.songs_master_id}-${option.name}`}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>
                {option.name}
              </Typography>
              {option.artist && (
                <Typography variant="caption" sx={{ color: '#ccc' }}>
                  by {option.artist}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        loading={songsLoading}
        noOptionsText="No songs found. Try a different search term."
        clearOnBlur={false}
        autoHighlight
        ListboxProps={{
          style: {
            maxHeight: '300px', // Limit height for performance with large lists
          }
        }}
      />

      {/* Selected Songs with DnD */}
      {selectedSongs.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
            Selected Songs ({selectedSongs.length}) - Drag to reorder:
          </Typography>
          <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
            The order of songs determines clue mapping: Clue 1 → First song, Clue 2 → Second song, etc.
          </Alert>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onSongReorder}
          >
            <SortableContext
              items={selectedSongs.map(s => s.songs_master_id)}
              strategy={verticalListSortingStrategy}
            >
              {selectedSongs.map((song, index) => (
                <Box key={song.songs_master_id}>
                  <SortableSongItem song={song} onRemove={onSongRemove} />
                  <Box sx={{ ml: 8, mb: 1 }}>
                    <Chip
                      label={`Clue ${index + 1} → ${song.name}`}
                      size="small"
                      sx={{
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}
    </Box>
  );
};

// File Upload Step Component
const FileUploadStep = ({
  uploadedFile,
  setUploadedFile,
  isDragOver,
  setIsDragOver,
  isProcessing,
  setIsProcessing,
  setParsedData,
  setValidationResults,
  duplicateGroups,
  setDuplicateGroups,
  detectDuplicates,
  selectedSongs,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleFileSelect = (file) => {
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/csv'
    ];

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      enqueueSnackbar('Please select a valid Excel file (.xlsx, .xls) or CSV file (.csv)', { variant: 'error' });
      return;
    }

    setUploadedFile(file);
    processFile(file);
  };

  const processFile = async (file) => {
    setIsProcessing(true);
    try {
      const parsedQuestions = await parseFileContent(file);
      setParsedData(parsedQuestions);

      // Basic validation for each question
      const validationResults = parsedQuestions.map((question) => {
        const errors = [];

        // Basic validation
        if (!question.questionText || question.questionText.trim().length < 10) {
          errors.push('Question text must be at least 10 characters');
        }

        if (!question.answerType) {
          errors.push('Answer type is required');
        }

        if (!question.difficulty) {
          errors.push('Difficulty is required');
        }

        if (!question.answers || question.answers.length < 2) {
          errors.push('At least 2 answers are required');
        }

        const correctAnswers = question.answers?.filter(a => a.correct).length || 0;
        if (correctAnswers === 0) {
          errors.push('At least one answer must be correct');
        }

        if (question.answerType === 'single_choice' && correctAnswers !== 1) {
          errors.push('Single choice questions must have exactly one correct answer');
        }

        if (question.answerType === 'multiple_choice' && correctAnswers < 1) {
          errors.push('Multiple choice questions must have at least one correct answer');
        }

        if (question.answerType === 'multiple_choice' && question.answers?.length < 3) {
          errors.push('Multiple choice questions should have at least 3 answer options');
        }

        if (question.answerType === 'true_false' && question.answers?.length !== 2) {
          errors.push('True/False questions must have exactly 2 answers');
        }

        if (question.answerType === 'true_false' && correctAnswers !== 1) {
          errors.push('True/False questions must have exactly one correct answer');
        }

        return {
          isValid: errors.length === 0,
          errors: errors
        };
      });

      setValidationResults(validationResults);

      // Detect duplicates after parsing
      const duplicates = detectDuplicates(parsedQuestions);
      setDuplicateGroups(duplicates);

    } catch (error) {
      console.error('File processing error:', error);
      enqueueSnackbar(`Error processing file: ${error.message}`, { variant: 'error' });
      setParsedData([]);
      setValidationResults([]);
      setDuplicateGroups([]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to parse file content
  const parseFileContent = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

          let data = [];

          if (fileExtension === '.csv') {
            // Parse CSV
            data = parseCSVContent(content);
          } else {
            // For Excel files, we'll need to use a library like xlsx
            // For now, show an error message
            reject(new Error('Excel file parsing requires server-side processing. Please use CSV format for client-side preview.'));
            return;
          }

          // Detect clue columns
          const clueColumns = [];
          Object.keys(data[0] || {}).forEach((header) => {
            const normalizedHeader = header.trim().toLowerCase();
            const match = normalizedHeader.match(/^clue\s*(\d+)$/);
            if (match) {
              const clueNumber = parseInt(match[1]);
              clueColumns.push({
                header: header,
                number: clueNumber
              });
            }
          });
          clueColumns.sort((a, b) => a.number - b.number);

          // Validate clue count matches song count
          if (clueColumns.length > 0 && selectedSongs.length > 0) {
            if (clueColumns.length !== selectedSongs.length) {
              throw new Error(`Clue count mismatch: CSV has ${clueColumns.length} clue column(s) but ${selectedSongs.length} song(s) selected. Please ensure clue count matches song count.`);
            }
          }

          // Convert parsed data to our question format
          const questions = data.map((row, index) => {
            const answers = [];

            // Extract answers (up to 4)
            for (let i = 1; i <= 4; i++) {
              const answerText = row[`Answer ${i}`];
              const isCorrect = row[`Answer ${i} Correct`];
              const answerReason = row[`Answer ${i} Reason`];

              if (answerText && answerText.trim()) {
                answers.push({
                  text: answerText.trim(),
                  correct: isCorrect === 'TRUE' || isCorrect === 'true' || isCorrect === '1' || isCorrect === 'Yes',
                  reason: answerReason || ''
                });
              }
            }

            // Extract clues
            const clues = clueColumns.map(clueCol => ({
              number: clueCol.number,
              text: row[clueCol.header]?.trim() || ''
            }));

            return {
              rowNumber: index + 2, // +2 because Excel is 1-indexed and has header
              questionText: row['Question Text'] || '',
              questionType: row['Question Type'] || 'text',
              answerType: row['Answer Type'] || '',
              difficulty: row['Difficulty'] || '',
              // Support both "Question Mood"/"Mood" and "Question Topic"/"Topic" headers
              questionMood: row['Question Mood'] || row['Mood'] || '',
              questionTopic: row['Question Topic'] || row['Topic'] || '',
              questionBank: row['Question Bank'] || 'Songq',
              questionClue: row['Question Clue'] || '', // Fallback for single clue
              clues: clues, // Multiple clues
              answers: answers
            };
          });

          resolve(questions);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  };

  // Helper function to parse CSV content
  const parseCSVContent = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Parse header row
    const headers = parseCSVLine(lines[0]);

    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length > 0 && values.some(v => v.trim())) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  // Helper function to parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const downloadTemplate = (format = 'csv') => {
    const headers = [
      'Question Text', 'Question Type', 'Answer Type', 'Difficulty',
      'Question Mood', 'Question Topic', 'Question Bank',
      'Question Clue', 'Answer 1', 'Answer 1 Correct', 'Answer 1 Reason',
      'Answer 2', 'Answer 2 Correct', 'Answer 2 Reason',
      'Answer 3', 'Answer 3 Correct', 'Answer 3 Reason',
      'Answer 4', 'Answer 4 Correct', 'Answer 4 Reason'
    ];

    const sampleData = [
      // Single choice example
      [
        'What year was this song released?', 'text', 'single_choice', 'Easy',
        'Upbeat', 'Music History', 'Songq', 'Think about the 1970s',
        '1975', 'TRUE', 'Released in 1975', '1973', 'FALSE', 'Too early',
        '1977', 'FALSE', 'Too late', '', '', ''
      ],
      // Multiple choice example
      [
        'Which genres does this song represent?', 'text', 'multiple_choice', 'Medium',
        'Energetic', 'Music Theory', 'Songq', 'This song blends multiple styles',
        'Rock', 'TRUE', 'Strong rock elements', 'Pop', 'TRUE', 'Has mainstream appeal',
        'Classical', 'FALSE', 'No classical influences', 'Jazz', 'FALSE', 'No jazz elements'
      ],
      // True/False example
      [
        'This song was written by John Lennon', 'text', 'true_false', 'Hard',
        'Calm', 'Music History', 'Songq', 'Think about Beatles songwriting',
        'True', 'TRUE', 'John Lennon was the writer', 'False', 'FALSE', 'This is incorrect',
        '', '', '', '', '', ''
      ]
    ];

    if (format === 'csv') {
      // Create CSV content
      const csvRows = [headers.join(',')];
      sampleData.forEach(row => {
        csvRows.push(row.map(cell => `"${cell}"`).join(','));
      });
      const csvContent = csvRows.join('\n') + '\n';

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'quiz_questions_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      // Create Excel content using XLSX (if available) or fallback to CSV
      try {
        // This would require importing XLSX library on frontend
        // For now, fallback to CSV
        downloadTemplate('csv');
      } catch (error) {
        downloadTemplate('csv');
      }
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        Upload Excel File
      </Typography>

      <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
        Upload an Excel file (.xlsx, .xls) or CSV file (.csv) containing quiz questions.
        Make sure the file follows the required format. Download a template below to get started.
      </Alert>

      <Alert severity="warning" sx={{ mb: 3, backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
        <strong>Note:</strong> For immediate preview in the review step, use CSV format.
        Excel files will be processed during import but won't show preview data.
      </Alert>

      {/* Download Template Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <ActionButton
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => downloadTemplate('csv')}
        >
          Download CSV Template
        </ActionButton>
        <ActionButton
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => downloadTemplate('excel')}
        >
          Download Excel Template
        </ActionButton>
      </Box>

      {/* File Upload Area */}
      <UploadArea
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />

        {isProcessing ? (
          <Box>
            <CircularProgress sx={{ color: '#4CAF50', mb: 2 }} />
            <Typography sx={{ color: '#fff', mb: 1 }}>Processing file...</Typography>
            <Typography sx={{ color: '#ccc', fontSize: '0.9em' }}>
              Parsing {uploadedFile?.name} and validating question format
            </Typography>
          </Box>
        ) : uploadedFile ? (
          <Box>
            <CheckIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
            <Typography sx={{ color: '#fff', mb: 1 }}>
              File uploaded successfully
            </Typography>
            <Typography sx={{ color: '#ccc' }}>
              {uploadedFile.name}
            </Typography>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
              {isDragOver ? 'Drop your file here' : 'Drop your file here or click to browse'}
            </Typography>
            <Typography sx={{ color: '#ccc', fontSize: '0.9em' }}>
              Supports Excel (.xlsx, .xls) and CSV (.csv) files up to 10MB
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.8em', mt: 1 }}>
              Make sure your file follows the template format
            </Typography>
          </Box>
        )}
      </UploadArea>
    </Box>
  );
};

// Review Step Component
const ReviewStep = ({
  parsedData,
  setParsedData,
  validationResults,
  setValidationResults,
  selectedSongs,
  uploadedFile,
  duplicateGroups,
  setDuplicateGroups,
  duplicateSettings,
  setDuplicateSettings,
  onEditRow,
  onDeleteRow,
  onRemoveDuplicates,
  detectDuplicates,
  importMutation,
  onSuccess,
  onClose
}) => {
  // Detect duplicates when parsedData changes
  React.useEffect(() => {
    if (parsedData.length > 0) {
      const duplicates = detectDuplicates(parsedData);
      setDuplicateGroups(duplicates);
    }
  }, [parsedData, detectDuplicates, setDuplicateGroups]);

  const validQuestions = parsedData.filter((_, index) => validationResults[index]?.isValid);
  const invalidQuestions = parsedData.filter((_, index) => !validationResults[index]?.isValid);

  // Check if a row is a duplicate
  const isDuplicate = (index) => {
    return duplicateGroups.some(group => group.indices.includes(index));
  };

  // Get duplicate group for a row
  const getDuplicateGroup = (index) => {
    return duplicateGroups.find(group => group.indices.includes(index));
  };

  const handleAutoRemoveDuplicates = () => {
    let updatedData = [...parsedData];
    let updatedValidation = [...validationResults];
    const indicesToRemove = [];

    // For each duplicate group, keep the first occurrence and mark others for removal
    duplicateGroups.forEach(group => {
      const [, ...removeIndices] = group.indices; // Keep first, remove rest
      indicesToRemove.push(...removeIndices);
    });

    // Remove duplicates in reverse order to maintain indices
    indicesToRemove.sort((a, b) => b - a).forEach(index => {
      updatedData.splice(index, 1);
      updatedValidation.splice(index, 1);
    });

    setParsedData(updatedData);
    setValidationResults(updatedValidation);

    // Clear duplicate groups after removal
    setDuplicateGroups([]);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        Review Questions Before Import
      </Typography>

      {/* Clue to Song Mapping */}
      {selectedSongs.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
          <Typography sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
            Clue to Song Mapping:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedSongs.map((song, index) => (
              <Chip
                key={song.songs_master_id}
                avatar={<Avatar src={getImageUrl(song.image)} sx={{ width: 24, height: 24 }}><MusicNoteIcon sx={{ fontSize: 14 }} /></Avatar>}
                label={`Clue ${index + 1} → ${song.name}`}
                sx={{
                  backgroundColor: '#4CAF50',
                  color: '#fff',
                }}
              />
            ))}
          </Box>
        </Alert>
      )}

      {parsedData.length > 0 ? (
        <>
          {/* Enhanced Summary with Duplicate Detection */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={<CheckIcon />}
              label={`${validQuestions.length} Valid Questions`}
              sx={{ backgroundColor: '#4CAF50', color: '#fff' }}
            />
            {invalidQuestions.length > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${invalidQuestions.length} Invalid Questions`}
                sx={{ backgroundColor: '#f44336', color: '#fff' }}
              />
            )}
            {duplicateGroups.length > 0 && (
              <Chip
                icon={<DuplicateIcon />}
                label={`${duplicateGroups.reduce((sum, group) => sum + group.count - 1, 0)} Duplicates Found`}
                sx={{ backgroundColor: '#FF9800', color: '#fff' }}
              />
            )}
            <Chip
              label={`${parsedData.length} Total Questions`}
              sx={{ backgroundColor: '#2196F3', color: '#fff' }}
            />
          </Box>

          {/* Duplicate Management Controls */}
          {duplicateGroups.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3, backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#fff' }}>
                  <strong>{duplicateGroups.length} duplicate group(s) detected.</strong>
                  Questions with identical text have been identified.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAutoRemoveDuplicates}
                  sx={{
                    borderColor: '#FF9800',
                    color: '#FF9800',
                    '&:hover': {
                      borderColor: '#FF9800',
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    },
                  }}
                >
                  Auto-Remove Duplicates
                </Button>
              </Box>
            </Alert>
          )}
        </>
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 3, backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
            <Typography sx={{ color: '#fff' }}>
              <strong>Excel File Detected:</strong> {uploadedFile?.name}
            </Typography>
            <Typography sx={{ color: '#ccc', mt: 1 }}>
              Preview is not available for Excel files. The file will be processed and validated during import.
              Questions will be validated against the database and any errors will be reported after processing.
            </Typography>
          </Alert>

          {/* Show action message for Excel files */}
          <Alert severity="info" sx={{ mb: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
            <Typography sx={{ color: '#fff' }}>
              <strong>Enhanced Import Features Available:</strong>
            </Typography>
            <Typography sx={{ color: '#ccc', mt: 1 }}>
              • Automatic duplicate detection and removal<br/>
              • Row-level editing and deletion capabilities<br/>
              • Real-time validation and error reporting<br/>
              • Complete control over your import data
            </Typography>
            <Typography sx={{ color: '#4CAF50', mt: 1, fontWeight: 600 }}>
              All features will be available during the import process!
            </Typography>
          </Alert>
        </>
      )}

      {/* Enhanced Questions Table with Row Actions */}
      {parsedData.length > 0 && (
        <TableContainer component={Paper} sx={{ backgroundColor: '#121212', maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 60 }}>Status</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 50 }}>Row</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 250 }}>Question Text</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 100 }}>Question Type</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 100 }}>Answer Type</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 80 }}>Difficulty</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 100 }}>Mood</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 100 }}>Topic</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 80 }}>Bank</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 150 }}>Question Clue</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 200 }}>Answers</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 200 }}>Issues</TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', color: '#fff', minWidth: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedData.map((question, index) => {
                const validation = validationResults[index];
                const duplicateGroup = getDuplicateGroup(index);
                const isRowDuplicate = isDuplicate(index);

                return (
                  <TableRow
                    key={index}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#1A1A1A' },
                      backgroundColor: isRowDuplicate ? 'rgba(255, 152, 0, 0.1)' : undefined,
                      border: isRowDuplicate ? '1px solid #FF9800' : undefined
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {validation?.isValid ? (
                          <CheckIcon sx={{ color: '#4CAF50' }} />
                        ) : (
                          <ErrorIcon sx={{ color: '#f44336' }} />
                        )}
                        {isRowDuplicate && (
                          <DuplicateIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {question.rowNumber}
                        {isRowDuplicate && (
                          <Chip
                            size="small"
                            label={`Dup ${duplicateGroup?.indices.indexOf(index) + 1}/${duplicateGroup?.count}`}
                            sx={{
                              backgroundColor: '#FF9800',
                              color: '#fff',
                              fontSize: '0.7em',
                              height: 20
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Question Text */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {question.questionText}
                      </Typography>
                    </TableCell>

                    {/* Question Type */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">{question.questionType || 'text'}</Typography>
                    </TableCell>

                    {/* Answer Type */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">{question.answerType}</Typography>
                    </TableCell>

                    {/* Difficulty */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">{question.difficulty}</Typography>
                    </TableCell>

                    {/* Mood */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">{question.questionMood}</Typography>
                    </TableCell>

                    {/* Topic */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">{question.questionTopic}</Typography>
                    </TableCell>

                    {/* Bank */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">{question.questionBank || 'Songq'}</Typography>
                    </TableCell>

                    {/* Question Clues */}
                    <TableCell sx={{ color: '#fff' }}>
                      {question.clues && question.clues.length > 0 ? (
                        <Box>
                          {question.clues.map((clue, idx) => (
                            <Typography key={idx} variant="caption" sx={{ display: 'block', mb: 0.5, color: '#ccc' }}>
                              <strong>Clue {clue.number}:</strong> {clue.text || '-'}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {question.questionClue || '-'}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Answers - Display Only (Complex editing would need separate modal) */}
                    <TableCell sx={{ color: '#fff' }}>
                      <Box>
                        {question.answers?.map((answer, answerIndex) => (
                          <Box key={answerIndex} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{
                              color: answer.correct ? '#4CAF50' : '#ccc',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              {answer.correct ? '✓' : '✗'} {answer.text}
                              {answer.reason && (
                                <Typography component="span" sx={{ color: '#999', fontSize: '0.7em' }}>
                                  ({answer.reason})
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        )) || (
                            <Typography variant="caption" sx={{ color: '#999' }}>
                              No answers
                            </Typography>
                          )}
                      </Box>
                    </TableCell>

                    {/* Issues */}
                    <TableCell sx={{ color: '#fff' }}>
                      {validation?.errors?.length > 0 ? (
                        <Box>
                          {validation.errors.map((error, i) => (
                            <Typography key={i} variant="caption" sx={{
                              color: '#f44336',
                              display: 'block',
                              mb: 0.5
                            }}>
                              • {error}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                          Valid
                        </Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => onEditRow(index)}
                          sx={{ color: '#2196F3' }}
                          title="Edit question"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this question?')) {
                              onDeleteRow(index);
                            }
                          }}
                          sx={{ color: '#f44336' }}
                          title="Delete question"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {isRowDuplicate && duplicateGroup && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const groupIndex = duplicateGroups.findIndex(g => g === duplicateGroup);
                              if (groupIndex !== -1) {
                                onRemoveDuplicates(groupIndex, index);
                              }
                            }}
                            sx={{ color: '#FF9800' }}
                            title="Keep this, remove other duplicates"
                          >
                            <DuplicateIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// Import Button Component
const ImportButton = ({
  uploadedFile,
  parsedData,
  selectedSongs,
  importMutation,
  onSuccess,
  onClose
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const handleImport = async () => {
    if (!uploadedFile && (!parsedData || parsedData.length === 0)) {
      enqueueSnackbar('No file selected or data to import', { variant: 'warning' });
      return;
    }

    if (selectedSongs.length === 0) {
      enqueueSnackbar('No songs selected', { variant: 'warning' });
      return;
    }

    // Validate clue count matches song count
    if (parsedData && parsedData.length > 0) {
      const firstQuestion = parsedData[0];
      const clueCount = firstQuestion.clues?.length || 0;
      if (clueCount > 0 && clueCount !== selectedSongs.length) {
        enqueueSnackbar(`Clue count mismatch: ${clueCount} clues but ${selectedSongs.length} songs selected`, { variant: 'error' });
        return;
      }
    }

    setIsImporting(true);
    setImportProgress({ current: 0, total: 1 });

    try {
      let totalInserted = 0;
      let totalSkipped = 0;
      const allResults = [];

      // Use processed data (CSV with clues)
      if (parsedData && parsedData.length > 0) {
        enqueueSnackbar(`Importing ${parsedData.length} question(s) for ${selectedSongs.length} song(s)...`, { variant: 'info' });

        // Convert clues to format expected by backend
        const processedQuestions = parsedData.map(q => ({
          ...q,
          clues: q.clues || (q.questionClue ? [{ text: q.questionClue }] : [])
        }));

        const processedImportData = {
          processedQuestions: processedQuestions,
          songs_master_ids: selectedSongs.map(s => s.songs_master_id),
          isProcessedData: true
        };

        const result = await importMutation.mutateAsync(processedImportData);
        totalInserted += result.summary?.inserted || 0;
        totalSkipped += result.summary?.skipped || 0;
        allResults.push({
          songs: selectedSongs.map(s => s.name).join(', '),
          result: result
        });
      } else if (uploadedFile) {
        // Use file upload method for Excel files
        const importData = {
          file: uploadedFile,
          songs_master_ids: selectedSongs.map(s => s.songs_master_id)
        };

        const result = await importMutation.mutateAsync(importData);
        totalInserted += result.summary?.inserted || 0;
        totalSkipped += result.summary?.skipped || 0;
        allResults.push({
          songs: selectedSongs.map(s => s.name).join(', '),
          result: result
        });
      }

      // Show detailed success message
      const successMessage = `Import completed! ${totalInserted} question entries created, ${totalSkipped} skipped`;
      enqueueSnackbar(successMessage, { variant: 'success' });

      // Show detailed results if there were any issues
      if (totalSkipped > 0) {
        const allErrors = allResults
          .flatMap(r => r.result.errors || [])
          .filter(err => err);

        if (allErrors.length > 0) {
          // Log all errors to console
          console.error('❌ Import Errors:', allErrors);
          
          // Show first few errors in snackbar
          const firstErrors = allErrors.slice(0, 3);
          enqueueSnackbar(
            `${totalSkipped} question(s) skipped. ${firstErrors.length > 0 ? `Errors: ${firstErrors.join('; ')}` : 'Check console for details.'}`,
            { 
              variant: 'error',
              autoHideDuration: 10000,
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
            }
          );

          // Also show in alert for better visibility
          if (allErrors.length > 3) {
            setTimeout(() => {
              alert(`Import completed with ${totalSkipped} skipped questions.\n\nFirst ${Math.min(5, allErrors.length)} errors:\n${allErrors.slice(0, 5).join('\n')}\n\nCheck browser console for all ${allErrors.length} errors.`);
            }, 500);
          }
        } else {
          enqueueSnackbar(
            `${totalSkipped} question(s) skipped. Check server console for details.`,
            { variant: 'warning' }
          );
        }
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('❌ Import error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to import questions';

      const errorDetails = error.response?.data?.details || '';
      const fullErrorMessage = errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage;

      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 10000
      });

      // Show additional error details if available
      if (error.response?.data?.errors?.length > 0) {
        console.error('❌ Detailed errors:', error.response.data.errors);
        setTimeout(() => {
          alert(`Import failed!\n\n${fullErrorMessage}\n\nErrors:\n${error.response.data.errors.slice(0, 10).join('\n')}${error.response.data.errors.length > 10 ? `\n\n... and ${error.response.data.errors.length - 10} more errors. Check console.` : ''}`);
        }, 500);
      } else if (errorDetails) {
        setTimeout(() => {
          alert(`Import failed!\n\n${fullErrorMessage}`);
        }, 500);
      }
    } finally {
      setIsImporting(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  return (
    <Box>
      {isImporting && importProgress.total > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
            Processing song {importProgress.current} of {importProgress.total}
          </Typography>
          <Box sx={{
            width: '100%',
            height: 4,
            backgroundColor: '#333',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <Box sx={{
              width: `${(importProgress.current / importProgress.total) * 100}%`,
              height: '100%',
              backgroundColor: '#4CAF50',
              transition: 'width 0.3s ease'
            }} />
          </Box>
        </Box>
      )}

      <ActionButton
        variant="contained"
        onClick={handleImport}
        disabled={!uploadedFile || selectedSongs.length === 0 || isImporting}
        startIcon={isImporting ? <CircularProgress size={20} /> : null}
      >
        {isImporting ?
          `Importing... (${importProgress.current}/${importProgress.total})` :
          `Import Questions`
        }
      </ActionButton>
    </Box>
  );
};

export default QuizQuestionImportModal;
